import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  InputAdornment,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { Search, ArrowUp, ArrowDown, MessageCircle, ArrowLeft, Link2 } from "lucide-react";
import { toast } from "react-toastify";
import LogoLoader from "./UI/LogoLoader";

const StatuteDetail = ({ toggleAskChatbot, closeOnBack, selectedSection }) => {
  const itemRefs = useRef([]);
  const statuteSections = useSelector((state) => state.statuteSections);
  const { loading, sections } = statuteSections;
  const statuteTitle = useSelector((state) => state.statuteTitle);
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  
  const { title: statuteName } = statuteTitle;
  const [totalMatches, setTotalMatches] = React.useState(0);
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedElements, setHighlightedElements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const highlightText = (html, search, section) => {
    const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const escapedSearch = search ? escapeRegex(search) : null;

    // Compile regular expressions
    const regexSearch = escapedSearch
      ? new RegExp(
          `(${escapedSearch.replace(
            /\s+/g,
            "(\\s|<[^>]+>|\\n|\\r|&nbsp;|<\\/?.+?>)*"
          )})`,
          "gi"
        )
      : null;

    // Helper function for applying highlights
    const applyHighlight = (text, regex, className) => {
      return text.replace(
        regex,
        `<span class="${className}" style="background-color: ${
          className === "highlight" ? "#FEF3C7" : "#DCFCE7"
        }; font-weight: 600; padding: 2px 4px; border-radius: 4px;">$1</span>`
      );
    };

    // Recursive function to apply highlights in text nodes
    const highlightNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        let highlightedText = node.textContent;
        if (regexSearch) {
          highlightedText = applyHighlight(
            highlightedText,
            regexSearch,
            "highlight"
          );
        }
        return highlightedText;
      }

      // If it's an element, recursively process its children
      if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes)
          .map(highlightNode)
          .join("");
        const element = document.createElement(node.nodeName.toLowerCase());
        Array.from(node.attributes).forEach((attr) =>
          element.setAttribute(attr.name, attr.value)
        );
        element.innerHTML = children;
        return element.outerHTML;
      }
      return "";
    };

    // Parse the HTML string, apply highlights, and return the final HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return Array.from(tempDiv.childNodes).map(highlightNode).join("");
  };

  const handleCopyLink = () => {
    if (!statuteName) return;
    const link = `${window.location.origin}/app/statute-search/${encodeURIComponent(statuteName)}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const handleNextMatch = () => {
    if (highlightedElements.length > 0) {
      const nextIndex = (currentIndex + 1) % highlightedElements.length;
      setCurrentIndex(nextIndex);
      setCurrentMatch(nextIndex + 1);
    }
  };

  const handlePreviousMatch = () => {
    if (highlightedElements.length > 0) {
      const prevIndex =
        (currentIndex - 1 + highlightedElements.length) %
        highlightedElements.length;
      setCurrentIndex(prevIndex);
      setCurrentMatch(prevIndex + 1);
    }
  };

  useEffect(() => {
    if (!searchTerm) {
      setCurrentIndex(-1);
      setHighlightedElements([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    // Only query highlights when searchTerm changes
    const highlights = document.querySelectorAll(".highlight");
    setHighlightedElements(Array.from(highlights));
    setTotalMatches(highlights.length);
    setCurrentMatch(highlights.length > 0 ? 1 : 0);
  }, [searchTerm]);

  useEffect(() => {
    if (highlightedElements.length > 0) {
      highlightedElements.forEach((el, index) => {
        if (index === currentIndex) {
          el.style.backgroundColor = "#F59E0B";
          el.style.color = "#FFFFFF";
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          el.style.backgroundColor = "#FEF3C7";
          el.style.color = "#92400E";
        }
      });
    }
  }, [currentIndex, highlightedElements]);

  useEffect(() => {
    // Scroll to the specific index if the selectedSection is valid
    if (selectedSection !== null && itemRefs.current[selectedSection]) {
      itemRefs.current[selectedSection].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedSection]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: 1,
        backgroundColor: "#FFFFFF",
      }}
    >
      {/* Header with Search and Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "20px 24px",
          borderBottom: "1px solid #F1F5F9",
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* Mobile Back Button */}
        <Box sx={{ display: { xs: "flex", lg: "none" } }}>
          <IconButton
            onClick={closeOnBack}
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: "#F8FAFC",
              color: "#64748B",
              "&:hover": {
                backgroundColor: "#F1F5F9",
                color: "#1E293B",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
        </Box>

        {/* Search Bar */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search within this statute..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  borderColor: "#CBD5E1",
                },
                "&.Mui-focused": {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#00509e",
                  boxShadow: "0 0 0 3px rgba(0, 80, 158, 0.1)",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                color: "#1E293B",
                "&::placeholder": {
                  color: "#94A3B8",
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 2 }}>
                  <Search size={20} color="#64748B" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end" sx={{ mr: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={`${currentMatch}/${totalMatches}`}
                      size="small"
                      sx={{
                        backgroundColor: "#F0F9FF",
                        color: "#00509e",
                        fontSize: "12px",
                        fontWeight: "600",
                        height: "24px",
                        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handlePreviousMatch}
                      disabled={totalMatches === 0}
                      sx={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "6px",
                        backgroundColor: "#F8FAFC",
                        color: totalMatches === 0 ? "#CBD5E1" : "#64748B",
                        "&:hover": {
                          backgroundColor: totalMatches === 0 ? "#F8FAFC" : "#F1F5F9",
                          color: totalMatches === 0 ? "#CBD5E1" : "#1E293B",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <ArrowUp size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleNextMatch}
                      disabled={totalMatches === 0}
                      sx={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "6px",
                        backgroundColor: "#F8FAFC",
                        color: totalMatches === 0 ? "#CBD5E1" : "#64748B",
                        "&:hover": {
                          backgroundColor: totalMatches === 0 ? "#F8FAFC" : "#F1F5F9",
                          color: totalMatches === 0 ? "#CBD5E1" : "#1E293B",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <ArrowDown size={16} />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Copy Link */}
        <IconButton
          onClick={handleCopyLink}
          title="Copy Link"
          sx={{
            width: "40px",
            height: "40px",
            borderRadius: "10px",
            backgroundColor: "#F0F9FF",
            color: "#00509e",
            "&:hover": {
              backgroundColor: "#E0F2FE",
              color: "#003366",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          <Link2 size={20} />
        </IconButton>

        {/* Mobile Chat Button */}
        <Box sx={{ display: { xs: "flex", lg: "none" } }}>
          <IconButton
            onClick={toggleAskChatbot}
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: "#F0F9FF",
              color: "#00509e",
              "&:hover": {
                backgroundColor: "#E0F2FE",
                color: "#003366",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <MessageCircle size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Content Area */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            padding: "40px 20px",
          }}
        >
          <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "500",
              color: "#64748B",
              fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            }}
          >
            Loading statute content...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
            "::-webkit-scrollbar": {
              width: "8px",
            },
            "::-webkit-scrollbar-track": {
              background: "#F8FAFC",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#CBD5E1",
              borderRadius: "8px",
              "&:hover": {
                background: "#94A3B8",
              },
            },
          }}
        >
          {sections?.[0]?.related_data
            ?.sort((a, b) => {
              if (a.Section === "PREAMBLE") return -1;
              if (b.Section === "PREAMBLE") return 1;
              const sectionA = parseFloat(a.Section);
              const sectionB = parseFloat(b.Section);
              return sectionA - sectionB;
            })
            .map((result, index) => (
              <Box
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                sx={{
                  marginBottom: "24px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "16px",
                  border: "1px solid #F1F5F9",
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#E2E8F0",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                {/* Section Header */}
                <Box
                  sx={{
                    padding: "20px 24px 16px 24px",
                    borderBottom: "1px solid #F1F5F9",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: "8px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        backgroundColor: "#00509e",
                        color: "#FFFFFF",
                        fontSize: "14px",
                        fontWeight: "600",
                        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      {result.Section === "PREAMBLE" ? "0" : result.Section}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1E293B",
                        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      {result.Section === "PREAMBLE" ? "Preamble" : `Section ${result.Section}`}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#64748B",
                      fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      lineHeight: "1.5",
                    }}
                  >
                    {result.Definition}
                  </Typography>
                </Box>

                {/* Section Content */}
                <Box sx={{ padding: "24px" }}>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      lineHeight: "1.7",
                      color: "#334155",
                      fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      textAlign: "justify",
                      "& p": {
                        marginBottom: "16px",
                        "&:last-child": {
                          marginBottom: 0,
                        },
                      },
                      "& h1, & h2, & h3, & h4, & h5, & h6": {
                        color: "#1E293B",
                        fontWeight: "600",
                        marginTop: "24px",
                        marginBottom: "12px",
                      },
                      "& ul, & ol": {
                        paddingLeft: "24px",
                        marginBottom: "16px",
                      },
                      "& li": {
                        marginBottom: "8px",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: highlightText(
                        result.Statute_HTML,
                        searchTerm,
                        selectedSection
                      ),
                    }}
                  />
                </Box>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default StatuteDetail;
